export const PURCHASE_OPTIONS = [
  {
    type: "piece",
    label: "Buy Piece",
    displayType: "Piece",
    staticDiscountPercentage: 10,
  },
  {
    type: "pack",
    label: "Buy Pack",
    displayType: "Pack",
    staticDiscountPercentage: 20,
  },
  {
    type: "carton",
    label: "Buy Carton",
    displayType: "Carton",
    staticDiscountPercentage: 30,
  },
];

const CART_PURCHASE_KEY = "cartPurchaseOptions";

export const getPurchaseOptionByType = (purchaseType = "piece") =>
  PURCHASE_OPTIONS.find(option => option.type === purchaseType) ||
  PURCHASE_OPTIONS[0];

const getProductDetail = (product = {}) => product?.dtl?.[0] || {};

const getNumberField = (
  product = {},
  fieldNames = [],
  fallback = 0,
) => {
  const detail = getProductDetail(product);

  for (const fieldName of fieldNames) {
    const value = Number(
      product?.[fieldName] ?? detail?.[fieldName],
    );

    if (!isNaN(value)) {
      return value;
    }
  }

  return fallback;
};

/**
 * Backend Fields
 *
 * minorderqty -> Piece Qty
 * packqty     -> Pack Qty
 * cartqty     -> Carton Qty
 */

export const getMinOrderQty = product =>
  getNumberField(product, ["minorderqty"], 1);

export const getPackQty = product =>
  getNumberField(product, ["packqty"], 0);

export const getCartonQty = product =>
  getNumberField(product, ["cartqty"], 0);

export const getExtra1Qty = product =>
  getNumberField(product, ["extra1qty"], 0);

export const getExtra2Qty = product =>
  getNumberField(product, ["extra2qty"], 0);

export const getExtra3Qty = product =>
  getNumberField(product, ["extra3qty"], 0);

export const isSingleUnitProduct = (product = {}) => {
  const packqty = getPackQty(product);
  const cartqty = getCartonQty(product);

  return packqty === 0 && cartqty === 0;
};

/**
 * Show modal only when multiple quantity discount options exist
 */

export const shouldShowPurchaseModal = (product = {}) => {
  return (product?.discount_items?.length ?? 0) > 1;
};

/**
 * Modal Options
 *
 * Rules:
 *
 * Always show Piece Qty
 *
 * If packqty == cartqty
 * hide Pack Qty and show only Carton Qty
 *
 * If packqty == 0 && cartqty == 0
 * show only Piece Qty
 */

export const getQuantityOptions = (product = {}) => {
  const minorderqty = getMinOrderQty(product);
  const packqty = getPackQty(product);
  const cartqty = getCartonQty(product);

  const options = [];

  // Always show Piece Qty
  options.push({
    type: "piece",
    label: "Piece Qty",
    quantity: minorderqty,
    discount: getDiscountPercentage(product, "piece"),
  });

  // No popup case
  if (packqty === 0 && cartqty === 0) {
    return options;
  }

  // Show Pack only if different from Carton
  if (packqty > 0 && packqty !== cartqty) {
    options.push({
      type: "pack",
      label: "Pack Qty",
      quantity: packqty,
      discount: getDiscountPercentage(product, "pack"),
    });
  }

  // Show Carton
  if (cartqty > 0) {
    options.push({
      type: "carton",
      label: "Carton Qty",
      quantity: cartqty,
      discount: getDiscountPercentage(product, "carton"),
    });
  }

  return options;
};

/**
 * Increment/Decrement Step
 */

export const getPurchaseQuantityStep = (
  product = {},
  purchaseType = "piece",
) => {
  switch (purchaseType) {
    case "piece":
      return getMinOrderQty(product);

    case "pack":
      return getPackQty(product);

    case "carton":
      return getCartonQty(product);

    case "extra1":
      return getExtra1Qty(product) || getMinOrderQty(product);

    case "extra2":
      return getExtra2Qty(product) || getMinOrderQty(product);

    case "extra3":
      return getExtra3Qty(product) || getMinOrderQty(product);

    default:
      return 1;
  }
};

export const getMinimumPurchaseQuantity = (
  product = {},
  purchaseType = "piece",
) => {
  switch (purchaseType) {
    case "piece":
      return getMinOrderQty(product);

    case "pack":
      return getPackQty(product);

    case "carton":
      return getCartonQty(product);

    case "extra1":
      return getExtra1Qty(product) || getMinOrderQty(product);

    case "extra2":
      return getExtra2Qty(product) || getMinOrderQty(product);

    case "extra3":
      return getExtra3Qty(product) || getMinOrderQty(product);

    default:
      return getMinOrderQty(product);
  }
};

export const getUnitPrice = (product = {}) => {
  const detail = getProductDetail(product);

  const savedUnitPrice = Number(product?.unitPrice);
  const netrate = Number(product?.netrate ?? detail?.netrate);
  const gross = Number(product?.gross);
  const qty = Number(product?.qty) || 1;
  const mrp = Number(product?.mrp ?? detail?.mrp);
  const discount = Number(
    product?.discount ?? detail?.discount ?? 0,
  );

  if (savedUnitPrice > 0) return savedUnitPrice;

  if (netrate > 0) return netrate;

  if (gross > 0) return gross / qty;

  if (mrp > 0 && discount > 0) {
    return mrp - (mrp * discount) / 100;
  }

  return mrp > 0 ? mrp : 0;
};

export const getDiscountPercentage = (
  product = {},
  purchaseType = "piece",
) => {
  const option = getPurchaseOptionByType(purchaseType);

  return Number(
    product?.discount_percentage ??
    product?.dtl?.[0]?.discount_percentage ??
    option.staticDiscountPercentage,
  );
};

export const buildCartPurchaseData = (
  product = {},
  purchaseType = "piece",
) => {
  const option = getPurchaseOptionByType(purchaseType);

  const detail = getProductDetail(product);

  const productId =
    product?.productId ||
    product?.productid ||
    detail?.productid ||
    product?.id;

  const minorderqty = getMinOrderQty(product);
  const packqty = getPackQty(product);
  const cartqty = getCartonQty(product);

  let quantity = minorderqty;

  if (purchaseType === "pack") {
    quantity = packqty;
  }

  if (purchaseType === "carton") {
    quantity = cartqty;
  }

  const unitPrice = getUnitPrice(product);

  const discountPercentage = getDiscountPercentage(
    product,
    purchaseType,
  );

  const originalTotal = unitPrice * quantity;

  const discountAmount =
    (originalTotal * discountPercentage) / 100;

  const finalPrice = originalTotal - discountAmount;

  return {
    productId,
    purchaseType: option.type,
    quantity,
    minorderqty,
    packqty,
    cartqty,
    unitPrice,
    discountPercentage,
    discountAmount,
    finalPrice,
  };
};

export const calculateCartPurchaseData = (
  item = {},
  purchaseType = "piece",
  quantity,
) => {
  const baseData = buildCartPurchaseData(
    item,
    purchaseType,
  );

  const minimumQty = getMinimumPurchaseQuantity(
    item,
    purchaseType,
  );

  const selectedQty = Math.max(
    Number(quantity ?? item?.qty ?? baseData.quantity) ||
    baseData.quantity,
    minimumQty,
  );

  const originalTotal =
    baseData.unitPrice * selectedQty;

  const discountAmount =
    (originalTotal * baseData.discountPercentage) / 100;

  return {
    ...baseData,
    quantity: selectedQty,
    discountAmount,
    finalPrice: originalTotal - discountAmount,
  };
};

const readPurchaseStore = () => {
  try {
    return JSON.parse(
      localStorage.getItem(CART_PURCHASE_KEY) || "{}",
    );
  } catch {
    return {};
  }
};

export const saveCartPurchaseData = purchaseData => {
  if (!purchaseData?.productId) return;

  const store = readPurchaseStore();

  store[String(purchaseData.productId)] = purchaseData;

  localStorage.setItem(
    CART_PURCHASE_KEY,
    JSON.stringify(store),
  );
};

export const getSavedCartPurchaseData = productId => {
  if (!productId) return null;

  return readPurchaseStore()[String(productId)] || null;
};

export const removeSavedCartPurchaseData = productId => {
  if (!productId) return;

  const store = readPurchaseStore();

  delete store[String(productId)];

  localStorage.setItem(
    CART_PURCHASE_KEY,
    JSON.stringify(store),
  );
};

export const getQtyDiscountSlab = (product, qty) => {
  const discounts = product?.Qtydiscounts || product?.dtl?.[0]?.Qtydiscounts || [];
  const activeCoupons = discounts
    .filter((coupon) => String(coupon.active ?? "1") === "1")
    .slice()
    .sort((a, b) => Number(a.fromqty) - Number(b.fromqty));
  const quantity = Number(qty);

  if (!activeCoupons.length || quantity < Number(activeCoupons[0].fromqty)) {
    return null;
  }

  const matchingCoupon = activeCoupons.find(
    (coupon) =>
      quantity >= Number(coupon.fromqty) &&
      quantity <= Number(coupon.toqty),
  );

  if (matchingCoupon) return matchingCoupon;

  const lastCoupon = activeCoupons[activeCoupons.length - 1];
  return quantity > Number(lastCoupon.toqty) ? lastCoupon : null;
};

export const getApplicableCoupon = (quantity, qtyDiscounts = []) =>
  getQtyDiscountSlab({ Qtydiscounts: qtyDiscounts }, quantity);

export const getApplicableExtraDiscount = (quantity, discountItems = []) => {
  const currentQuantity = Number(quantity);
  const applicableItems = discountItems
    .filter((item) => Number(item.qty) <= currentQuantity)
    .slice()
    .sort((a, b) => Number(b.qty) - Number(a.qty));

  return applicableItems[0] || null;
};

export const getQuantityStep = (product = {}) =>
  Math.max(1, getMinOrderQty(product));

export const getNextQuantity = (quantity, product = {}) =>
  Number(quantity || 0) + getQuantityStep(product);

export const getPreviousQuantity = (quantity, product = {}) =>
  Math.max(
    Number(quantity || 0) - getQuantityStep(product),
    getQuantityStep(product),
  );

export const getDiscountBadgeText = (
  productDiscount,
  extraDiscount,
  dealerDiscount,
) => {
  const discounts = [productDiscount, extraDiscount, dealerDiscount]
    .map(Number)
    .filter((discount) => discount > 0)
    .map((discount) => `${discount}%`);

  return discounts.length ? `${discounts.join(" + ")} OFF` : "";
};

export const getExtraDiscountBadgeText = (extraDiscount) => {
  const discount = Number(extraDiscount);
  return discount > 0 ? `Extra Discount ${discount}%` : "";
};

export const recalculateCartItemDiscount = (cartItem) => {
  if (!cartItem || !cartItem.dtl?.[0]) return cartItem;

  const qty = Number(cartItem.qty) || 1;
  const productDetails = cartItem.dtl[0];
  const savedPurchaseData = getSavedCartPurchaseData(productDetails.productid);
  const Qtydiscounts = [
    productDetails.Qtydiscounts,
    cartItem.Qtydiscounts,
    savedPurchaseData?.Qtydiscounts,
  ].find((discounts) => Array.isArray(discounts) && discounts.length) || [];
  const discountItems = [
    productDetails.discount_items,
    cartItem.discount_items,
    savedPurchaseData?.discount_items,
  ].find((items) => Array.isArray(items) && items.length) || [];
  const productDiscount = Number(
    productDetails.discount ??
    savedPurchaseData?.productDiscount ??
    cartItem.disp ??
    0,
  );
  const perUnitMrp =
    Number(productDetails.mrp || 0) ||
    Number(savedPurchaseData?.mrp || 0) ||
    Number(cartItem.mrp) / qty;

  const slab = Qtydiscounts.length
    ? getQtyDiscountSlab({ Qtydiscounts }, qty)
    : null;
  const extraDiscountItem = getApplicableExtraDiscount(qty, discountItems);
  const applicableCoupon = slab || extraDiscountItem;
  const extraDiscount = applicableCoupon ? Number(applicableCoupon.discount) : 0;
  const basePrice = perUnitMrp - (perUnitMrp * productDiscount) / 100;
  const specialDiscPerUnit = (basePrice * extraDiscount) / 100;
  const splamount = specialDiscPerUnit * qty;
  const unitPriceAfterExtra = basePrice - specialDiscPerUnit;
  const gross = unitPriceAfterExtra * qty;

  return {
    ...cartItem,
    Qtydiscounts,
    discount_items: discountItems,
    applicableCoupon,
    spldis: extraDiscount,
    splamount,
    gross,
    disp: productDiscount + extraDiscount,
  };
};

export const getFinalPrice = (product, selectedQty, extraDiscount) => {
  const mrp = Number(product.mrp) || 0;
  const slab = getQtyDiscountSlab(product, selectedQty);
  const productDiscount = Number(product.discount || 0);
  const couponDiscount = extraDiscount !== undefined
    ? Number(extraDiscount)
    : Number(slab?.discount || 0);
  const basePrice = mrp - (mrp * productDiscount) / 100;
  const price = basePrice - (basePrice * couponDiscount) / 100;
  return price.toFixed(2);
};

export const buildEnhancedPurchaseData = (product, purchaseType, couponData) => {
  const baseData = buildCartPurchaseData(product, purchaseType);
  if (!couponData) return baseData;

  const qty = Number(couponData.qty) || baseData.quantity;
  const productDiscount = Number(product.discount || 0);
  const extraDiscount = Number(couponData.discount) || 0;
  const unitPrice = baseData.unitPrice;
  const originalTotal = unitPrice * qty;

  return {
    ...baseData,
    quantity: qty,
    finalPrice: originalTotal * (1 - baseData.discountPercentage / 100),
    discountAmount: originalTotal * (baseData.discountPercentage / 100),
    purchaseType,
    appliedCouponQty: qty,
    extraDiscount,
    productDiscount,
    totalDiscount: productDiscount + extraDiscount,
    couponSelected: true,
    minimumCouponQty: qty,
  };
};

const PENDING_COUPON_KEY = "pendingCouponSelection";

export const savePendingCouponSel = (productId, purchaseType, couponData) => {
  if (!productId || !couponData) return;
  const store = JSON.parse(localStorage.getItem(PENDING_COUPON_KEY) || "{}");
  store[String(productId)] = {
    purchaseType,
    minimumCouponQty: couponData.qty,
    appliedCouponQty: couponData.qty,
    extraDiscount: couponData.discount,
    couponSelected: true,
    couponTitle: couponData.title || "",
  };
  localStorage.setItem(PENDING_COUPON_KEY, JSON.stringify(store));
};

export const getPendingCouponSel = (productId) => {
  if (!productId) return null;
  const store = JSON.parse(localStorage.getItem(PENDING_COUPON_KEY) || "{}");
  return store[String(productId)] || null;
};

export const clearPendingCouponSel = (productId) => {
  if (!productId) return;
  const store = JSON.parse(localStorage.getItem(PENDING_COUPON_KEY) || "{}");
  delete store[String(productId)];
  localStorage.setItem(PENDING_COUPON_KEY, JSON.stringify(store));
};

export const formatPrice = (value) => Number(value || 0).toFixed(2);